import { useCallback, useEffect, useRef } from "react";

import { useDispatch } from "react-redux";

import { setMessage } from "@/redux/notification";
import { saveRecordingNotes } from "@/services/recordingOwnership";

import { useRecordingDock } from "./store/useRecordingDock";

const DEBOUNCE_MS = 1500;

/**
 * Autosaves the shared Assessment Notes to the owning case's assessment
 * `notes` field. Owned by the root RecordingHost so it fires whether
 * the notes are edited from the scribe Notes field or the floating dock, and
 * whether or not the scribe page is mounted.
 *
 * The notes "stand alone" — the field is replaced with exactly the current
 * text. Writes are debounced while a session is live and flushed on
 * stop; a dedupe ref skips redundant writes.
 */
export const useNotesAutosave = () => {
  const dispatch = useDispatch();

  const caseNumber = useRecordingDock((s) => s.caseNumber);
  const notes = useRecordingDock((s) => s.notes);
  const status = useRecordingDock((s) => s.status);
  const setNotesSaveState = useRecordingDock((s) => s.setNotesSaveState);

  // Last value we successfully wrote — skips redundant saves. Null means
  // "nothing written this session yet".
  const lastSavedRef = useRef<string | null>(null);

  const isLive =
    status === "connecting" || status === "recording" || status === "paused";

  const persist = useCallback(async () => {
    if (!caseNumber) return;
    const next = notes.trim();
    // Nothing to save and nothing previously written this session.
    if (!next && lastSavedRef.current === null) return;
    if (next === lastSavedRef.current) return;

    setNotesSaveState("saving");
    try {
      await saveRecordingNotes(caseNumber, next);
      lastSavedRef.current = next;
      setNotesSaveState("saved", Date.now());
    } catch (error) {
      // A silent failure would lose the notes with no indication.
      console.error("Failed to persist session notes:", error);
      setNotesSaveState("error");
      dispatch(
        setMessage({ content: "Failed to save session notes", type: "error" })
      );
    }
  }, [caseNumber, notes, setNotesSaveState, dispatch]);

  // Reset the dedupe when a fresh session begins so a new session's notes can't
  // be skipped as "same as the previous session's last save".
  useEffect(() => {
    if (status === "connecting") lastSavedRef.current = null;
  }, [status]);

  // Debounced autosave while live.
  useEffect(() => {
    if (!isLive) return;
    const t = setTimeout(() => void persist(), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [notes, isLive, persist]);

  // Flush on stop so notes typed in the last debounce window are saved.
  const flushedForStopRef = useRef(false);
  useEffect(() => {
    if (status !== "stopped") {
      flushedForStopRef.current = false;
      return;
    }
    if (flushedForStopRef.current) return;
    flushedForStopRef.current = true;
    void persist();
  }, [status, persist]);
};
